/**
 * VoiceCampaigns.jsx — Iter 206.
 *
 * Lightweight campaign runner. A campaign holds a queue of leads/contacts;
 * SDRs dial through the queue one lead at a time (via MCube click-to-call).
 * Progress + outcomes are captured per job so managers can see conversion.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Megaphone, Play, Pause, Plus, PhoneCall, ChevronRight, CheckCircle2,
  Trash2, RefreshCw, ListFilter, Rocket, Sparkles, Users, Upload,
  Phone, Bot, Loader2, X, LayoutDashboard, Zap, Settings2, RotateCw,
  AlertTriangle, BarChart3, Download, MessageSquare, Check,
} from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import WhatsappDraftDialog from "@/components/voice/WhatsappDraftDialog";

const OUTCOMES = [
  { value: "connected", label: "Connected", terminal: false },
  { value: "no_answer", label: "No answer", terminal: false },
  { value: "busy", label: "Busy", terminal: false },
  { value: "voicemail", label: "Voicemail", terminal: false },
  { value: "callback", label: "Callback requested", terminal: false },
  { value: "not_interested", label: "Not interested", terminal: true },
  { value: "booked_meeting", label: "Booked meeting 🎉", terminal: true },
  { value: "do_not_call", label: "Do-not-call", terminal: true },
  { value: "failed", label: "Call failed", terminal: false },
];

const STATUS_BADGE = {
  draft: "bg-foreground/10 text-foreground/70 border-foreground/25",
  active: "bg-emerald-50 text-emerald-700 border-emerald-500/40",
  paused: "bg-amber-50 text-amber-700 border-amber-500/40",
  completed: "bg-blue-50 text-blue-700 border-blue-500/40",
  cancelled: "bg-red-50 text-red-700 border-red-500/40",
};

export default function VoiceCampaigns() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [dashboardId, setDashboardId] = useState(null);

  const load = useCallback(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/voice-campaigns`)
      .then(({ data }) => setRows(data.rows || []))
      .catch(() => setRows([]));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (r, action) => {
    try {
      await api.post(`/workspaces/${wsId}/voice-campaigns/${r.id}/${action}`);
      toast.success(`Campaign ${action}d`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, `Couldn't ${action}`)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Existing job history is preserved.`)) return;
    try {
      await api.delete(`/workspaces/${wsId}/voice-campaigns/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-6" data-testid="voice-campaigns-page">
      <div className="flex items-center gap-3">
        <Megaphone className="w-6 h-6 text-primary" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Voice Campaigns</h1>
          <div className="text-xs text-muted-foreground">
            Schedule bulk outbound calls to a lead list. Dial through the queue in one click.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="vc-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
        <Button className="rounded-none" onClick={() => setCreating(true)} data-testid="vc-new">
          <Plus className="w-3 h-3 mr-1" /> New campaign
        </Button>
      </div>

      {rows === null && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
      <VoiceReadinessBanner wsId={wsId} />
      {rows && rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center" data-testid="vc-empty">
          <Rocket className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="font-semibold">No campaigns yet</div>
          <div className="text-xs text-muted-foreground">
            Create a campaign, pick a lead source, and start dialing.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows && rows.map((r) => (
          <div key={r.id} className="border border-border p-4 space-y-3"
               data-testid={`vc-card-${r.id}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                  {r.name}
                  {r.ai_only && (
                    <Badge
                      className="rounded-none text-[9px] bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-300"
                      data-testid={`vc-ai-only-badge-${r.id}`}
                    >
                      AI-only
                    </Badge>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {r.channel} · {r.telephony_provider || "mcube"} · created {(r.created_at || "").slice(0, 10)}
                </div>
              </div>
              <Badge className={`rounded-none text-[10px] border ${STATUS_BADGE[r.status] || ""}`}>
                {r.status}
              </Badge>
            </div>
            {r.stats && (
              <div className="grid grid-cols-5 gap-1 text-center text-[10px]">
                <StatBox label="Total" value={r.stats.total} />
                <StatBox label="Pending" value={r.stats.pending} tone="amber" />
                <StatBox label="In Prog" value={r.stats.in_progress} tone="blue" />
                <StatBox label="Done" value={r.stats.completed} tone="emerald" />
                <StatBox label="Failed" value={r.stats.failed} tone="red" />
              </div>
            )}
            {r.script_notes && (
              <div className="text-[11px] italic border-l-2 border-primary/40 pl-2 line-clamp-2">
                {r.script_notes}
              </div>
            )}
            <div className="flex items-center gap-1 pt-2 border-t border-border">
              {r.status === "draft" || r.status === "paused" ? (
                <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                         onClick={() => toggle(r, "start")} data-testid={`vc-start-${r.id}`}>
                  <Play className="w-3 h-3 mr-1" /> Start
                </Button>
              ) : null}
              {r.status === "active" && (
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={() => toggle(r, "pause")} data-testid={`vc-pause-${r.id}`}>
                  <Pause className="w-3 h-3 mr-1" /> Pause
                </Button>
              )}
              <Button size="sm" className="rounded-none"
                       onClick={() => setDashboardId(r.id)}
                       data-testid={`vc-dashboard-${r.id}`}>
                <LayoutDashboard className="w-3 h-3 mr-1" /> Dashboard
              </Button>
              {r.channel === "mcube" && !r.ai_only && (
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={() => setRunningId(r.id)}
                         disabled={r.status !== "active" && r.status !== "draft"}
                         data-testid={`vc-dial-${r.id}`}>
                  <PhoneCall className="w-3 h-3 mr-1" /> Dial next
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-none ml-auto text-red-600"
                       onClick={() => del(r)} data-testid={`vc-delete-${r.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {creating && (
        <CreateCampaignDialog wsId={wsId} onClose={() => setCreating(false)}
                                onCreated={() => { setCreating(false); load(); }} />
      )}
      {runningId && (
        <RunnerDialog wsId={wsId} campaignId={runningId}
                        onClose={() => { setRunningId(null); load(); }} />
      )}
      {dashboardId && (
        <CampaignDashboardDialog
          wsId={wsId}
          campaignId={dashboardId}
          onClose={() => { setDashboardId(null); load(); }}
        />
      )}
    </div>
  );
}


function StatBox({ label, value, tone, onClick, active, testid }) {
  const cls = {
    amber: "text-amber-700",
    blue: "text-blue-700",
    emerald: "text-emerald-700",
    red: "text-red-700",
    rose: "text-rose-700",
  }[tone] || "text-foreground";
  const base = "border border-border p-1";
  const clickable = onClick ? "cursor-pointer hover:bg-muted/40 transition-colors" : "";
  const ring = active ? "ring-2 ring-primary ring-offset-1" : "";
  return (
    <div
      className={`${base} ${clickable} ${ring}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      data-testid={testid}
    >
      <div className={`font-semibold ${cls}`}>{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}


/** Big red-flag banner that surfaces the two most common
 *  "my campaign isn't dialing" causes BEFORE the user has to open a
 *  campaign and read a cryptic queue error:
 *    1. ElevenLabs API key missing (no provider configured)
 *    2. Phone Number ID missing (carrier config empty)
 *  Both link straight to the ElevenLabs setup dialog on the Voice
 *  Agents → Providers tab via `?setup=elevenlabs`. */
function VoiceReadinessBanner({ wsId }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    (async () => {
      try {
        const [provs, carrier, mcube, camps] = await Promise.all([
          api.get(`/workspaces/${wsId}/voice-providers`),
          api.get(`/workspaces/${wsId}/voice-agent-carrier-config`)
            .catch(() => ({ data: {} })),
          api.get(`/workspaces/${wsId}/integrations/mcube`)
            .catch(() => ({ data: {} })),
          api.get(`/workspaces/${wsId}/voice-campaigns`)
            .catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        const el = (provs.data?.providers || []).find((p) => p.slug === "elevenlabs");
        const rows = camps.data?.rows || camps.data?.campaigns || [];
        const channels = new Set(rows.map((r) => r.channel).filter(Boolean));
        // AI-only campaigns dispatch through ElevenLabs regardless of
        // their nominal channel, so count them as ElevenLabs users
        // for the purpose of banner readiness.
        const anyAiOnly = rows.some((r) => r.ai_only);
        setState({
          elKey: !!(el && el.configured),
          phoneId: !!(carrier.data?.elevenlabs_phone_number_id),
          mcubeConfigured: !!(mcube.data?.configured),
          usesElevenlabs: channels.has("elevenlabs_convai")
                              || anyAiOnly || channels.size === 0,
          usesMcube: channels.has("mcube") && rows.some(
                             (r) => r.channel === "mcube" && !r.ai_only),
        });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [wsId]);
  if (!state) return null;
  const problems = [];
  // Only complain about ElevenLabs if the user actually uses that
  // channel (or has no campaigns yet, so we can't tell — in which
  // case we assume the default and warn once).
  if (state.usesElevenlabs && !state.elKey)   problems.push({ k: "elkey",    label: "ElevenLabs API key",    code: "elevenlabs_api_key_missing" });
  if (state.usesElevenlabs && !state.phoneId) problems.push({ k: "phoneid",  label: "Phone Number ID",       code: "elevenlabs_phone_number_id_missing" });
  if (state.usesMcube && !state.mcubeConfigured)
    problems.push({ k: "mcube", label: "MCube API key", code: "mcube_not_configured" });
  if (!problems.length) return null;
  // Build one or two fix links so a workspace missing both MCube and
  // ElevenLabs sees both call-to-actions, not just one.
  const needsMcube = problems.some((p) => p.k === "mcube");
  const needsEleven = problems.some((p) => p.k === "elkey" || p.k === "phoneid");
  return (
    <div className="border border-red-400 bg-red-50 p-3 flex items-start gap-2"
         data-testid="vc-readiness-banner">
      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
      <div className="flex-1 text-xs text-red-900">
        <div className="font-semibold mb-0.5">
          Voice campaigns can't dial yet — missing {problems.map((p) => p.label).join(" + ")}.
        </div>
        <div>
          Every call will fail with
          <code className="mx-1 bg-red-100 px-1 rounded">
            {problems[0].code}
          </code>
          until {problems.length === 1 ? "it's" : "they're"} set. Tap below to fix.
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-1">
        {needsMcube && (
          <a href="/app/settings?tab=integrations&open=mcube"
             className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-none"
             data-testid="vc-readiness-fix-mcube">
            Set MCube →
          </a>
        )}
        {needsEleven && (
          <a href="/app/voice-agents?tab=providers&setup=elevenlabs"
             className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-none"
             data-testid="vc-readiness-fix-eleven">
            Set ElevenLabs →
          </a>
        )}
      </div>
    </div>
  );
}


// ─── Create Campaign Dialog ───────────────────────────────────────

const AUDIENCE_TABS = [
  { id: "filter", label: "Lead filters", icon: ListFilter },
  { id: "manual", label: "Manual numbers", icon: Phone },
  { id: "csv", label: "CSV upload", icon: Upload },
  { id: "segment", label: "Saved segment", icon: Users },
];

function CreateCampaignDialog({ wsId, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", channel: "mcube",
    voice_agent_id: "",
    script_notes: "",
    first_message_override: "",
    pitch_prompt_override: "",
    max_attempts_per_lead: 3,
    auto_process: true,
    concurrency: 3,
    retry_delay_minutes: 30,
    budget_cap: null,
    ai_only: false,
    audience_source: "filter",
    lead_status: "", lead_source: "",
    segment_id: "",
    manual_contacts: [],  // [{phone, name, variables}]
  });
  const [saving, setSaving] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewSample, setPreviewSample] = useState([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [segments, setSegments] = useState([]);
  const [voiceAgents, setVoiceAgents] = useState([]);
  const [capPresets, setCapPresets] = useState([100, 500, 2000]);
  const [manualText, setManualText] = useState("");
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvPhoneCol, setCsvPhoneCol] = useState("");
  const [csvNameCol, setCsvNameCol] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load segments + agents once
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/lead-segments`)
      .then((r) => setSegments(r.data?.rows || r.data || []))
      .catch(() => setSegments([]));
    api.get(`/workspaces/${wsId}/voice-agents`)
      .then((r) => setVoiceAgents(r.data?.rows || []))
      .catch(() => setVoiceAgents([]));
    api.get(`/workspaces/${wsId}/voice-agent-cost-config`)
      .then((r) => {
        const p = r.data?.budget_cap_presets;
        if (Array.isArray(p) && p.length) setCapPresets(p);
      })
      .catch(() => {});
  }, [wsId]);

  // Parse manual-numbers textarea → array
  useEffect(() => {
    if (form.audience_source !== "manual") return;
    const rows = manualText
      .split(/\n+/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        // Supports "+9199...,Name" or just "+9199..."
        const [phone, ...rest] = l.split(/[,\t]/);
        return {
          phone: (phone || "").trim(),
          name: rest.join(",").trim() || null,
          variables: {},
        };
      })
      .filter((r) => r.phone);
    set("manual_contacts", rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualText, form.audience_source]);

  // CSV file parser
  const onCsvFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
    if (lines.length === 0) {
      toast.error("CSV looks empty");
      return;
    }
    const splitCsv = (line) => {
      const out = [];
      let cur = ""; let inQ = false;
      for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (inQ) {
          if (ch === '"') {
            if (line[i + 1] === '"') { cur += '"'; i += 1; }
            else { inQ = false; }
          } else { cur += ch; }
        } else {
          if (ch === ',') { out.push(cur); cur = ""; }
          else if (ch === '"') { inQ = true; }
          else { cur += ch; }
        }
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const headers = splitCsv(lines[0]);
    const rows = lines.slice(1).map((l) => splitCsv(l));
    setCsvHeaders(headers);
    setCsvRows(rows);
    // Guess phone / name columns
    const guessPhone = headers.findIndex((h) =>
      /phone|mobile|number|contact/i.test(h));
    const guessName = headers.findIndex((h) =>
      /^name$|first|full.?name/i.test(h));
    setCsvPhoneCol(guessPhone >= 0 ? headers[guessPhone] : (headers[0] || ""));
    setCsvNameCol(guessName >= 0 ? headers[guessName] : "");
    toast.success(`Parsed ${rows.length} rows · ${headers.length} columns`);
  };

  // Materialise CSV rows into manual_contacts whenever col mapping changes.
  useEffect(() => {
    if (form.audience_source !== "csv") return;
    if (!csvHeaders.length || !csvPhoneCol) return;
    const idxPhone = csvHeaders.indexOf(csvPhoneCol);
    const idxName = csvNameCol ? csvHeaders.indexOf(csvNameCol) : -1;
    const contacts = csvRows.map((r) => {
      const phone = (r[idxPhone] || "").trim();
      if (!phone) return null;
      const name = idxName >= 0 ? (r[idxName] || "").trim() : null;
      const variables = {};
      csvHeaders.forEach((h, i) => {
        if (i === idxPhone || i === idxName) return;
        if (r[i]) variables[h] = r[i];
      });
      return { phone, name: name || null, variables };
    }).filter(Boolean);
    set("manual_contacts", contacts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvHeaders, csvRows, csvPhoneCol, csvNameCol, form.audience_source]);

  const previewAudience = useCallback(async () => {
    setPreviewBusy(true);
    try {
      const payload = { audience_source: form.audience_source };
      if (form.audience_source === "filter") {
        payload.lead_filter = {
          ...(form.lead_status ? { status: form.lead_status } : {}),
          ...(form.lead_source ? { source: form.lead_source } : {}),
        };
      } else if (form.audience_source === "segment") {
        if (!form.segment_id) {
          setPreviewCount(0); setPreviewSample([]);
          setPreviewBusy(false); return;
        }
        payload.segment_id = form.segment_id;
      } else {
        payload.manual_contacts = form.manual_contacts;
      }
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-campaigns/preview-audience`, payload);
      setPreviewCount(data.count ?? 0);
      setPreviewSample(data.sample || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    } finally { setPreviewBusy(false); }
  }, [wsId, form]);

  // Debounced auto-preview
  useEffect(() => {
    const t = setTimeout(previewAudience, 400);
    return () => clearTimeout(t);
  }, [previewAudience]);

  // ─── AI Pitch generator ───
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCtx, setAiCtx] = useState({
    product_or_offer: "", target_audience: "", goal: "",
    tone: "friendly", language: "en-IN", extra_notes: "",
  });
  const draftPitch = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-campaigns/generate-pitch`,
        {
          campaign_name: form.name || null,
          product_or_offer: aiCtx.product_or_offer || null,
          target_audience: aiCtx.target_audience || null,
          goal: aiCtx.goal || null,
          tone: aiCtx.tone,
          language: aiCtx.language,
          extra_notes: aiCtx.extra_notes || null,
        });
      setForm((f) => ({
        ...f,
        script_notes: data.script_notes_text || f.script_notes,
        first_message_override: data.first_message || f.first_message_override,
      }));
      toast.success("Pitch drafted — review before launching");
      setAiOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI pitch drafting failed"));
    } finally { setAiBusy(false); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    // Both channels need a voice agent when AI-only is on — AI-only
    // routes every call through ElevenLabs ConvAI regardless of the
    // selected channel.
    if ((form.channel === "elevenlabs_convai" || form.ai_only) && !form.voice_agent_id) {
      toast.error(form.ai_only
        ? "AI-only needs a voice agent — pick one or disable AI-only"
        : "Pick a voice agent for this channel");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        channel: form.channel,
        voice_agent_id: form.voice_agent_id || null,
        telephony_provider: form.channel === "elevenlabs_convai"
          ? "elevenlabs" : "mcube",
        script_notes: form.script_notes,
        first_message_override: form.first_message_override || null,
        pitch_prompt_override: form.pitch_prompt_override || null,
        max_attempts_per_lead: form.max_attempts_per_lead,
        auto_process: (form.channel === "elevenlabs_convai" || form.ai_only)
          ? !!form.auto_process : false,
        concurrency: Number(form.concurrency) || 3,
        retry_delay_minutes: Number(form.retry_delay_minutes) || 30,
        budget_cap: form.budget_cap === null || form.budget_cap === "" ? null : Number(form.budget_cap),
        ai_only: !!form.ai_only,
        audience_source: form.audience_source,
      };
      if (form.audience_source === "filter") {
        const filter = {};
        if (form.lead_status) filter.status = form.lead_status;
        if (form.lead_source) filter.source = form.lead_source;
        payload.lead_filter = Object.keys(filter).length ? filter : null;
      } else if (form.audience_source === "segment") {
        payload.segment_id = form.segment_id || null;
      } else {
        payload.manual_contacts = form.manual_contacts;
      }
      await api.post(`/workspaces/${wsId}/voice-campaigns`, payload);
      toast.success("Campaign created");
      onCreated?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Create failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vc-create">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-4 h-4" /> New voice campaign
            </DialogTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-none bg-gradient-to-r from-fuchsia-50 to-indigo-50 border-indigo-400/60 hover:border-indigo-500 text-indigo-700"
              onClick={() => setAiOpen(true)}
              data-testid="vc-ai-pitch"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              AI draft pitch
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                    placeholder="Q1 SMB reactivation" className="rounded-none"
                    data-testid="vc-name" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow">Channel</Label>
              <select value={form.channel} onChange={(e) => set("channel", e.target.value)}
                       className="h-10 w-full text-sm border border-border bg-background px-2 rounded-none"
                       data-testid="vc-channel">
                <option value="mcube">MCube · human SDR dials</option>
                <option value="elevenlabs_convai">ElevenLabs · AI agent dials</option>
              </select>
            </div>
            <div>
              <Label className="label-eyebrow">Max attempts / lead</Label>
              <Input type="number" min={1} max={10}
                      value={form.max_attempts_per_lead}
                      onChange={(e) => set("max_attempts_per_lead", Number(e.target.value))}
                      className="rounded-none" />
            </div>
          </div>

          {/* AI-only mode — applies to both channels. When ON the AI calls
              the customer directly (no human bridge). Recording + summary
              are stored against the lead; the assigned human agent is
              pinged after the call. If the customer asks for a human
              mid-call we bridge back via MCube. */}
          <label
            className={`flex items-start gap-3 border p-3 cursor-pointer transition ${
              form.ai_only
                ? "border-fuchsia-400 bg-fuchsia-50/50"
                : "border-border bg-background hover:border-fuchsia-400/60"
            }`}
            data-testid="vc-ai-only-toggle"
          >
            <input
              type="checkbox"
              checked={!!form.ai_only}
              onChange={(e) => {
                const v = e.target.checked;
                setForm((f) => ({
                  ...f,
                  ai_only: v,
                  // AI-only must auto-dispatch — manual mode doesn't
                  // make sense when there's no human in the loop.
                  auto_process: v ? true : f.auto_process,
                }));
              }}
              className="mt-1"
              data-testid="vc-ai-only-checkbox"
            />
            <div className="min-w-0">
              <div className="font-semibold text-xs flex items-center gap-1">
                <Bot className="w-3 h-3 text-fuchsia-600" />
                AI-only · no human bridge
              </div>
              <div className="text-[11px] text-muted-foreground">
                AI agent calls the customer directly via ElevenLabs. Recording and
                a short summary are saved on the lead and the assigned human agent
                is pinged after the call. If the customer asks for a human mid-call
                we auto-bridge via MCube.
              </div>
              {form.ai_only && form.channel === "mcube" && (
                <div className="text-[10px] text-amber-700 mt-1">
                  MCube channel + AI-only: calls will dispatch via ElevenLabs ConvAI
                  (needs a published voice agent + ElevenLabs phone number).
                </div>
              )}
            </div>
          </label>

          {(form.channel === "elevenlabs_convai" || form.ai_only) && (
            <div>
              <Label className="label-eyebrow">
                <Bot className="w-3 h-3 inline mr-1" />
                Voice agent *
              </Label>
              <select value={form.voice_agent_id}
                       onChange={(e) => set("voice_agent_id", e.target.value)}
                       className="h-10 w-full text-sm border border-border bg-background px-2 rounded-none"
                       data-testid="vc-voice-agent">
                <option value="">— Pick a published agent —</option>
                {voiceAgents.filter((a) => a.convai_agent_id).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {!voiceAgents.some((a) => a.convai_agent_id) && (
                <div className="text-[11px] text-amber-700 mt-1">
                  No published voice agents yet. Open Voice Agents → Publish one first.
                </div>
              )}
            </div>
          )}

          {(form.channel === "elevenlabs_convai" || form.ai_only) && (
            <div className="border border-indigo-300/60 bg-indigo-50/40 p-3 space-y-2"
                 data-testid="vc-auto-config">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <Zap className="w-3.5 h-3.5 text-indigo-600" />
                Dispatch mode
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-start gap-2 cursor-pointer border border-border p-2 bg-background has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="dispatch_mode"
                    checked={!!form.auto_process}
                    onChange={() => set("auto_process", true)}
                    className="mt-0.5"
                    data-testid="vc-mode-auto"
                  />
                  <div>
                    <div className="font-semibold text-xs">Auto</div>
                    <div className="text-[10px] text-muted-foreground">
                      AI dispatcher dials automatically every minute while the campaign is active.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer border border-border p-2 bg-background has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="dispatch_mode"
                    checked={!form.auto_process}
                    onChange={() => set("auto_process", false)}
                    className="mt-0.5"
                    data-testid="vc-mode-manual"
                  />
                  <div>
                    <div className="font-semibold text-xs">Manual</div>
                    <div className="text-[10px] text-muted-foreground">
                      You click "Process batch" in the dashboard to dial a batch.
                    </div>
                  </div>
                </label>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1">
                <div>
                  <Label className="label-eyebrow">Calls per minute</Label>
                  <Input type="number" min={1} max={10}
                          value={form.concurrency}
                          onChange={(e) => set("concurrency", Number(e.target.value))}
                          className="rounded-none h-9"
                          data-testid="vc-concurrency" />
                </div>
                <div>
                  <Label className="label-eyebrow">Retry delay (minutes)</Label>
                  <Input type="number" min={1} max={1440}
                          value={form.retry_delay_minutes}
                          onChange={(e) => set("retry_delay_minutes", Number(e.target.value))}
                          className="rounded-none h-9"
                          data-testid="vc-retry-delay" />
                </div>
                <div>
                  <Label className="label-eyebrow">Budget cap (₹, optional)</Label>
                  <Input type="number" min={0} step={1}
                          value={form.budget_cap ?? ""}
                          onChange={(e) => set("budget_cap",
                            e.target.value === "" ? null : Number(e.target.value))}
                          placeholder="e.g. 500"
                          className="rounded-none h-9"
                          data-testid="vc-budget-cap" />
                  <div className="flex items-center gap-1 flex-wrap mt-1">
                    {capPresets.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => set("budget_cap", p)}
                        data-testid={`vc-budget-preset-${p}`}
                        className={`text-[10px] px-1.5 py-0.5 border transition ${
                          Number(form.budget_cap) === p
                            ? "bg-emerald-600 text-white border-emerald-700"
                            : "border-border hover:border-emerald-600 hover:text-emerald-700"
                        }`}>
                        ₹{p}
                      </button>
                    ))}
                    {form.budget_cap != null && (
                      <button
                        type="button"
                        onClick={() => set("budget_cap", null)}
                        className="text-[10px] px-1.5 py-0.5 border border-dashed border-border text-muted-foreground hover:text-red-600 hover:border-red-500 transition"
                        data-testid="vc-budget-preset-clear">
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="text-[9px] text-muted-foreground mt-0.5">
                    Dialing auto-pauses when accrued cost ≥ cap.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Audience source tabs */}
          <div className="border border-border p-3 space-y-3">
            <div className="flex items-center gap-1 flex-wrap">
              {AUDIENCE_TABS.map((t) => {
                const Icon = t.icon;
                const active = form.audience_source === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`px-3 py-1.5 text-xs border -ml-px first:ml-0 flex items-center gap-1 ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-border hover:bg-muted/40"
                    }`}
                    onClick={() => set("audience_source", t.id)}
                    data-testid={`vc-audience-tab-${t.id}`}
                  >
                    <Icon className="w-3 h-3" /> {t.label}
                  </button>
                );
              })}
              <div className="ml-auto flex items-center gap-2 text-[11px]">
                {previewBusy
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Previewing…</>
                  : previewCount !== null && (
                      <span className="text-muted-foreground" data-testid="vc-preview-count">
                        <b className="text-foreground">{previewCount}</b> target{previewCount === 1 ? "" : "s"}
                      </span>
                    )}
              </div>
            </div>

            {form.audience_source === "filter" && (
              <div className="grid grid-cols-2 gap-2" data-testid="vc-audience-filter">
                <Input value={form.lead_status}
                        onChange={(e) => set("lead_status", e.target.value)}
                        placeholder="Status (e.g. new)" className="rounded-none"
                        data-testid="vc-filter-status" />
                <Input value={form.lead_source}
                        onChange={(e) => set("lead_source", e.target.value)}
                        placeholder="Source (e.g. website)" className="rounded-none"
                        data-testid="vc-filter-source" />
              </div>
            )}

            {form.audience_source === "manual" && (
              <div className="space-y-2" data-testid="vc-audience-manual">
                <Textarea
                  value={manualText}
                  onChange={(e) => setManualText(e.target.value)}
                  placeholder={"+919812345678\n+919812345679, Priya Sharma\n+919812345680, Ravi Kumar"}
                  rows={5}
                  className="rounded-none font-mono text-xs"
                  data-testid="vc-manual-numbers"
                />
                <div className="text-[11px] text-muted-foreground">
                  One number per line. Optional comma-separated name.
                </div>
              </div>
            )}

            {form.audience_source === "csv" && (
              <div className="space-y-2" data-testid="vc-audience-csv">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => onCsvFile(e.target.files?.[0])}
                  className="text-xs"
                  data-testid="vc-csv-file"
                />
                {csvHeaders.length > 0 && (
                  <div className="space-y-2 border border-border p-2 bg-muted/30">
                    <div className="text-[11px] text-muted-foreground">
                      Map columns — other columns are stored as merge variables ({"{{colname}}"}).
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="label-eyebrow">Phone column *</Label>
                        <select value={csvPhoneCol}
                                 onChange={(e) => setCsvPhoneCol(e.target.value)}
                                 className="h-9 w-full text-xs border border-border bg-background px-2 rounded-none"
                                 data-testid="vc-csv-phone-col">
                          {csvHeaders.map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="label-eyebrow">Name column</Label>
                        <select value={csvNameCol}
                                 onChange={(e) => setCsvNameCol(e.target.value)}
                                 className="h-9 w-full text-xs border border-border bg-background px-2 rounded-none"
                                 data-testid="vc-csv-name-col">
                          <option value="">— None —</option>
                          {csvHeaders.map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {csvRows.length} rows parsed. Variables available: {" "}
                      {csvHeaders
                        .filter((h) => h !== csvPhoneCol && h !== csvNameCol)
                        .map((h) => `{{${h}}}`).join(", ") || "(none)"}
                    </div>
                  </div>
                )}
              </div>
            )}

            {form.audience_source === "segment" && (
              <div data-testid="vc-audience-segment">
                <select value={form.segment_id}
                         onChange={(e) => set("segment_id", e.target.value)}
                         className="h-10 w-full text-sm border border-border bg-background px-2 rounded-none"
                         data-testid="vc-segment-select">
                  <option value="">— Pick a saved lead segment —</option>
                  {segments.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {segments.length === 0 && (
                  <div className="text-[11px] text-amber-700 mt-1">
                    No saved segments yet. Create one in CRM → Lead Segments.
                  </div>
                )}
              </div>
            )}

            {previewSample.length > 0 && (
              <div className="border-t border-border pt-2 space-y-1">
                <div className="label-eyebrow text-[10px]">Sample</div>
                {previewSample.map((s, i) => (
                  <div key={i} className="text-[11px] text-muted-foreground font-mono">
                    {s.phone} {s.name && <span className="not-italic">· {s.name}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="label-eyebrow">Script notes / talking points</Label>
            </div>
            <Textarea value={form.script_notes}
                       onChange={(e) => set("script_notes", e.target.value)}
                       placeholder={"Open with hook\nReference last campaign\nAsk for meeting"}
                       rows={5} className="rounded-none" data-testid="vc-script" />
          </div>

          {(form.channel === "elevenlabs_convai" || form.ai_only) && (
            <div>
              <Label className="label-eyebrow">First-message override (optional)</Label>
              <Input
                value={form.first_message_override}
                onChange={(e) => set("first_message_override", e.target.value)}
                placeholder="Hi {{name}}, this is Priya from Acme — do you have 60 seconds?"
                className="rounded-none"
                data-testid="vc-first-msg"
              />
            </div>
          )}
        </div>

        {aiOpen && (
          <Dialog open onOpenChange={(o) => !o && !aiBusy && setAiOpen(false)}>
            <DialogContent className="rounded-none max-w-md" data-testid="vc-ai-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" /> AI draft pitch
                </DialogTitle>
                <div className="text-xs text-muted-foreground">
                  Claude Opus 5.5 drafts the opening line, talking points, objection handling and CTA for this campaign.
                </div>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-eyebrow">Product / offer *</Label>
                  <Textarea
                    rows={3}
                    value={aiCtx.product_or_offer}
                    onChange={(e) => setAiCtx((c) => ({ ...c, product_or_offer: e.target.value }))}
                    placeholder="One-liner about what you sell + the hook"
                    className="rounded-none"
                    data-testid="vc-ai-product"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Target audience</Label>
                  <Input
                    value={aiCtx.target_audience}
                    onChange={(e) => setAiCtx((c) => ({ ...c, target_audience: e.target.value }))}
                    placeholder="SMB founders, D2C fashion, agency owners…"
                    className="rounded-none"
                    data-testid="vc-ai-audience"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Goal of the call</Label>
                  <Input
                    value={aiCtx.goal}
                    onChange={(e) => setAiCtx((c) => ({ ...c, goal: e.target.value }))}
                    placeholder="Book 15-min demo"
                    className="rounded-none"
                    data-testid="vc-ai-goal"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="label-eyebrow">Tone</Label>
                    <select value={aiCtx.tone}
                             onChange={(e) => setAiCtx((c) => ({ ...c, tone: e.target.value }))}
                             className="h-9 w-full text-xs border border-border bg-background px-2 rounded-none">
                      {["friendly", "professional", "urgent", "playful", "curious"].map((t) =>
                        <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="label-eyebrow">Language</Label>
                    <select value={aiCtx.language}
                             onChange={(e) => setAiCtx((c) => ({ ...c, language: e.target.value }))}
                             className="h-9 w-full text-xs border border-border bg-background px-2 rounded-none">
                      {[
                        ["en-IN", "English (India)"], ["en-US", "English (US)"],
                        ["hi", "हिंदी"], ["mr", "मराठी"], ["ta", "தமிழ்"],
                        ["te", "తెలుగు"], ["bn", "বাংলা"], ["gu", "ગુજરાતી"],
                        ["kn", "ಕನ್ನಡ"], ["ml", "മലയാളം"], ["pa", "ਪੰਜਾਬੀ"],
                      ].map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="label-eyebrow">Extra notes</Label>
                  <Textarea
                    rows={2}
                    value={aiCtx.extra_notes}
                    onChange={(e) => setAiCtx((c) => ({ ...c, extra_notes: e.target.value }))}
                    placeholder="e.g. avoid price, push demo signup"
                    className="rounded-none"
                    data-testid="vc-ai-notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-none"
                         onClick={() => setAiOpen(false)} disabled={aiBusy}>
                  Cancel
                </Button>
                <Button type="button" className="rounded-none"
                         onClick={draftPitch}
                         disabled={aiBusy || !aiCtx.product_or_offer.trim()}
                         data-testid="vc-ai-draft-go">
                  {aiBusy
                    ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Drafting…</>
                    : <><Sparkles className="w-3.5 h-3.5 mr-2" /> Draft with AI</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={create} disabled={saving} data-testid="vc-create-save">
            <Rocket className="w-3 h-3 mr-1" /> {saving ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Dialer Runner Dialog ─────────────────────────────────────────

function RunnerDialog({ wsId, campaignId, onClose }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [callId, setCallId] = useState(null);
  const [note, setNote] = useState("");

  const loadNext = useCallback(async () => {
    setLoading(true); setOutcome(""); setNotes(""); setCallId(null); setNote("");
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-campaigns/${campaignId}/jobs/next`);
      if (!data.job) {
        setJob(null);
        setNote(data.note || "No pending jobs");
      } else {
        setJob(data.job);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't fetch next job")); }
    finally { setLoading(false); }
  }, [wsId, campaignId]);

  useEffect(() => { loadNext(); }, [loadNext]);

  const dial = async () => {
    const person = job?.lead || job?.contact;
    if (!person?.phone) { toast.error("No phone number on this lead"); return; }
    try {
      const { data } = await api.post(`/workspaces/${wsId}/calls/click-to-call`, {
        customer_number: person.phone,
        customer_name: person.name || undefined,
        entity_type: job.lead_id ? "lead" : (job.contact_id ? "contact" : undefined),
        entity_id: job.lead_id || job.contact_id || undefined,
        entity_name: person.name || undefined,
      });
      setCallId(data.id);
      toast.success("Call initiated — pick up your phone");
    } catch (e) { toast.error(apiErrorMessage(e, "Dial failed")); }
  };

  const complete = async () => {
    if (!outcome) { toast.error("Pick an outcome"); return; }
    try {
      await api.post(
        `/workspaces/${wsId}/voice-campaigns/jobs/${job.id}/complete`,
        { outcome, call_id: callId, notes });
      toast.success("Outcome saved");
      loadNext();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const person = job?.lead || job?.contact;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="vc-runner">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="w-4 h-4" /> Dialer
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && !job && (
          <div className="p-6 text-center space-y-2">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
            <div className="font-semibold">Queue empty</div>
            <div className="text-xs text-muted-foreground">{note}</div>
          </div>
        )}
        {job && person && (
          <div className="space-y-3">
            <div className="border border-border p-3 space-y-1">
              <div className="text-lg font-semibold" data-testid="vc-runner-name">
                {person.name || "(no name)"}
              </div>
              <div className="text-xs text-muted-foreground">
                {person.company_name && `${person.company_name} · `}
                {person.email && `${person.email} · `}
                Attempt {job.attempts} of {job.campaign?.max_attempts_per_lead || 3}
              </div>
              <div className="text-2xl font-mono">{person.phone}</div>
            </div>
            {job.campaign?.script_notes && (
              <div className="border-l-2 border-primary/40 pl-3 text-xs italic max-h-24 overflow-y-auto whitespace-pre-wrap">
                {job.campaign.script_notes}
              </div>
            )}
            {!callId ? (
              <Button className="w-full rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                       onClick={dial} data-testid="vc-runner-dial">
                <PhoneCall className="w-4 h-4 mr-2" /> Click to call
              </Button>
            ) : (
              <div className="text-[11px] text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Call initiated (ID {callId.slice(0, 8)}…)
              </div>
            )}
            <div>
              <Label className="label-eyebrow">Outcome</Label>
              <select value={outcome} onChange={(e) => setOutcome(e.target.value)}
                       className="h-10 w-full text-sm border border-border bg-background px-2 rounded-none"
                       data-testid="vc-runner-outcome">
                <option value="">— Select —</option>
                {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-eyebrow">Notes (optional)</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                         className="rounded-none" data-testid="vc-runner-notes" />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="rounded-none" onClick={loadNext}>
                Skip
              </Button>
              <Button className="rounded-none ml-auto" onClick={complete}
                       data-testid="vc-runner-complete">
                <ChevronRight className="w-3 h-3 mr-1" /> Save + Next
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ─── Campaign Dashboard Dialog ────────────────────────────────────
//
// Live queue + progress + per-job retry + bulk retry + process-now +
// post-campaign summary. Replaces the single "Dial next" button for
// ElevenLabs AI campaigns and complements it for human (MCube) ones.

const JOB_STATUS_TONE = {
  pending: "bg-amber-50 text-amber-700 border-amber-500/40",
  in_progress: "bg-blue-50 text-blue-700 border-blue-500/40",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-500/40",
  failed: "bg-red-50 text-red-700 border-red-500/40",
  skipped: "bg-rose-50 text-rose-700 border-rose-500/40",
};

const OUTCOME_LABEL = {
  connected: "Connected",
  no_answer: "No answer",
  busy: "Busy",
  voicemail: "Voicemail",
  callback: "Callback",
  not_interested: "Not interested",
  booked_meeting: "Booked meeting",
  do_not_call: "Do-not-call",
  failed: "Failed",
  skipped_dnd: "Skipped (DND)",
};

function CampaignDashboardDialog({ wsId, campaignId, onClose }) {
  const [tab, setTab] = useState("queue");
  const [campaign, setCampaign] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Queue status filter — click a stats tile to narrow the table.
  // null = show everything; otherwise the exact `status` to match.
  const [queueFilter, setQueueFilter] = useState(null);
  // Feb 2026 · AI-only campaigns auto-draft a WhatsApp follow-up
  // after every completed call; this holds the row currently open
  // in the review-and-send dialog.
  const [waDraftJob, setWaDraftJob] = useState(null);

  const load = useCallback(async () => {
    if (!wsId || !campaignId) return;
    setLoading(true);
    try {
      const [c, j] = await Promise.all([
        api.get(`/workspaces/${wsId}/voice-campaigns/${campaignId}`),
        api.get(`/workspaces/${wsId}/voice-campaigns/${campaignId}/jobs?limit=500`),
      ]);
      setCampaign(c.data);
      setJobs(j.data?.rows || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not load campaign"));
    } finally { setLoading(false); }
  }, [wsId, campaignId]);

  const loadSummary = useCallback(async () => {
    if (!wsId || !campaignId) return;
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/voice-campaigns/${campaignId}/summary`);
      setSummary(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not load summary"));
    }
  }, [wsId, campaignId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "summary") loadSummary(); }, [tab, loadSummary]);

  // Auto-refresh every 5s on the queue tab while campaign is active.
  useEffect(() => {
    if (!autoRefresh || tab !== "queue") return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, tab, load]);

  const stats = useMemo(() => {
    const s = { total: jobs.length, pending: 0, in_progress: 0,
                 completed: 0, failed: 0, skipped: 0 };
    for (const j of jobs) s[j.status] = (s[j.status] || 0) + 1;
    s.pct = s.total ? Math.round(((s.completed + s.failed + s.skipped) / s.total) * 100) : 0;
    return s;
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    if (!queueFilter) return jobs;
    return jobs.filter((j) => j.status === queueFilter);
  }, [jobs, queueFilter]);

  const processNow = async () => {
    if (!campaign) return;
    if (campaign.channel !== "elevenlabs_convai" && !campaign.ai_only) {
      toast.info("Process-now is for AI campaigns. Human-SDR campaigns use the Dial next button.");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-campaigns/${campaignId}/process`);
      if (data.skipped) {
        toast.warning(`Campaign is ${data.skipped}`);
      } else {
        toast.success(`Dispatched ${data.dispatched || 0} · failed ${data.failed || 0}`);
        // Call-time DND toast — pop a one-liner with a "View skipped"
        // action so the user doesn't have to hunt the queue tile.
        if ((data.skipped_dnd || 0) > 0) {
          toast.warning(
            `${data.skipped_dnd} lead${data.skipped_dnd === 1 ? "" : "s"} skipped (DND)`,
            {
              duration: 10000,
              action: {
                label: "View skipped",
                onClick: () => {
                  setTab("queue");
                  setQueueFilter("skipped");
                },
              },
            });
        }
        if ((data.failed || 0) > 0 && data.results?.length) {
          const firstErr = data.results.find((r) => !r.ok && !r.skipped);
          if (firstErr) toast.error(firstErr.error || "Dispatch error", { duration: 8000 });
        }
      }
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Process failed"));
    } finally { setProcessing(false); }
  };

  const toggleAuto = async (val) => {
    try {
      await api.patch(`/workspaces/${wsId}/voice-campaigns/${campaignId}`,
                        { auto_process: val });
      toast.success(val ? "Auto-dispatch on" : "Auto-dispatch off");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not toggle")); }
  };

  const setConcurrency = async (val) => {
    try {
      await api.patch(`/workspaces/${wsId}/voice-campaigns/${campaignId}`,
                        { concurrency: Number(val) });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const toggleStatus = async (action) => {
    try {
      await api.post(`/workspaces/${wsId}/voice-campaigns/${campaignId}/${action}`);
      toast.success(`Campaign ${action}d`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, `${action} failed`)); }
  };

  const retryJob = async (jid) => {
    try {
      await api.post(`/workspaces/${wsId}/voice-campaigns/jobs/${jid}/retry`);
      toast.success("Re-queued");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Retry failed")); }
  };

  const retryAllFailed = async () => {
    if (!window.confirm("Re-queue every failed / no-answer / busy / voicemail job?")) return;
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-campaigns/${campaignId}/retry-failed`,
        { only_failed: true });
      toast.success(`Re-queued ${data.requeued || 0} jobs`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk retry failed")); }
  };

  const exportCsv = () => {
    const headers = ["name", "phone", "status", "attempts", "outcome",
                       "last_error", "conversation_id", "call_sid",
                       "last_dispatched_at", "completed_at"];
    const rows = jobs.map((j) => {
      const p = j.lead || j.contact || {};
      return [p.name || j.manual_name || "", p.phone || j.manual_phone || "",
                j.status || "", j.attempts || 0, j.outcome || "",
                (j.last_error || "").replace(/\n/g, " "),
                j.conversation_id || "", j.call_sid || "",
                j.last_dispatched_at || "", j.completed_at || ""];
    });
    const esc = (v) => {
      const s = String(v ?? "");
      return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(","),
                   ...rows.map((r) => r.map(esc).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${campaign?.name || "campaign"}_${campaignId.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto"
                      data-testid="vc-dashboard">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 truncate">
                <LayoutDashboard className="w-4 h-4" />
                {campaign?.name || "Campaign"}
              </DialogTitle>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {campaign?.ai_only
                  ? "AI-only · AI dials direct (ElevenLabs)"
                  : campaign?.channel === "elevenlabs_convai"
                  ? "ElevenLabs AI dialer"
                  : "Human SDR (MCube)"}
                {" · "}status <b>{campaign?.status || "…"}</b>
                {campaign?.auto_process && (
                  <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-indigo-400/60 bg-indigo-50 text-indigo-700 text-[10px]">
                    <Zap className="w-2.5 h-2.5" /> AUTO
                  </span>
                )}
                {campaign?.ai_only && (
                  <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-fuchsia-400/60 bg-fuchsia-50 text-fuchsia-700 text-[10px]"
                        data-testid="vc-dash-ai-only-badge">
                    <Bot className="w-2.5 h-2.5" /> AI-ONLY
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {campaign?.status === "active" && (
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={() => toggleStatus("pause")}
                         data-testid="vc-dash-pause">
                  <Pause className="w-3 h-3 mr-1" /> Pause
                </Button>
              )}
              {(campaign?.status === "paused" || campaign?.status === "draft") && (
                <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                         onClick={() => toggleStatus("start")}
                         data-testid="vc-dash-start">
                  <Play className="w-3 h-3 mr-1" /> Start
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-none"
                       onClick={load} data-testid="vc-dash-refresh">
                <RefreshCw className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Stats strip — click a tile to filter the queue table. */}
        <div className="grid grid-cols-7 gap-1 text-center text-[10px]">
          <StatBox label="Total" value={stats.total}
                    onClick={() => setQueueFilter(null)}
                    active={queueFilter === null}
                    testid="vc-stat-total" />
          <StatBox label="Pending" value={stats.pending} tone="amber"
                    onClick={() => setQueueFilter(stats.pending ? "pending" : null)}
                    active={queueFilter === "pending"}
                    testid="vc-stat-pending" />
          <StatBox label="In Prog" value={stats.in_progress} tone="blue"
                    onClick={() => setQueueFilter(stats.in_progress ? "in_progress" : null)}
                    active={queueFilter === "in_progress"}
                    testid="vc-stat-in-progress" />
          <StatBox label="Done" value={stats.completed} tone="emerald"
                    onClick={() => setQueueFilter(stats.completed ? "completed" : null)}
                    active={queueFilter === "completed"}
                    testid="vc-stat-completed" />
          <StatBox label="Failed" value={stats.failed} tone="red"
                    onClick={() => setQueueFilter(stats.failed ? "failed" : null)}
                    active={queueFilter === "failed"}
                    testid="vc-stat-failed" />
          <StatBox label="Skipped (DND)" value={stats.skipped} tone="rose"
                    onClick={() => setQueueFilter(stats.skipped ? "skipped" : null)}
                    active={queueFilter === "skipped"}
                    testid="vc-stat-skipped" />
          <StatBox label="Progress" value={`${stats.pct}%`} />
        </div>
        <div className="h-1.5 w-full bg-muted/40 border border-border">
          <div className="h-full bg-emerald-500" style={{ width: `${stats.pct}%` }} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border -mb-px">
          {[
            { id: "queue", label: "Queue", icon: PhoneCall },
            { id: "settings", label: "Settings", icon: Settings2 },
            { id: "summary", label: "Summary", icon: BarChart3 },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                className={`px-3 py-1.5 text-xs flex items-center gap-1 border-b-2 ${
                  active
                    ? "border-primary text-primary font-semibold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setTab(t.id)}
                data-testid={`vc-dash-tab-${t.id}`}
              >
                <Icon className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "queue" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {(campaign?.channel === "elevenlabs_convai" || campaign?.ai_only) && (
                <Button size="sm" className="rounded-none bg-indigo-600 hover:bg-indigo-700 text-white"
                         onClick={processNow} disabled={processing}
                         data-testid="vc-dash-process">
                  {processing
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Dispatching…</>
                    : <><Zap className="w-3 h-3 mr-1" /> Process batch</>}
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-none"
                       onClick={retryAllFailed}
                       data-testid="vc-dash-retry-all"
                       disabled={!stats.failed && !jobs.some((j) => j.outcome && j.status === "pending")}>
                <RotateCw className="w-3 h-3 mr-1" /> Retry failed
              </Button>
              <Button size="sm" variant="outline" className="rounded-none"
                       onClick={exportCsv} disabled={!jobs.length}
                       data-testid="vc-dash-export">
                <Download className="w-3 h-3 mr-1" /> Export CSV
              </Button>
              <label className="flex items-center gap-1 text-[11px] ml-auto cursor-pointer">
                <input type="checkbox" checked={autoRefresh}
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                        data-testid="vc-dash-autorefresh" />
                Live refresh
              </label>
            </div>
            {loading && !jobs.length && (
              <div className="text-xs text-muted-foreground p-4 text-center">Loading jobs…</div>
            )}
            {!loading && !jobs.length && (
              <div className="text-xs text-muted-foreground p-6 text-center border border-dashed border-border">
                No jobs in this campaign.
              </div>
            )}
            {jobs.length > 0 && queueFilter && (
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground border border-amber-500/40 bg-amber-50 px-2 py-1">
                Filtered to <b>{queueFilter.replace("_", " ")}</b> · {filteredJobs.length} job{filteredJobs.length === 1 ? "" : "s"}
                <button
                  type="button"
                  onClick={() => setQueueFilter(null)}
                  className="ml-auto underline hover:text-foreground"
                  data-testid="vc-queue-clear-filter"
                >
                  Clear filter
                </button>
              </div>
            )}
            {jobs.length > 0 && (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2 font-semibold">Contact</th>
                      <th className="p-2 font-semibold">Phone</th>
                      <th className="p-2 font-semibold">Status</th>
                      <th className="p-2 font-semibold">Attempts</th>
                      <th className="p-2 font-semibold">Outcome</th>
                      <th className="p-2 font-semibold">Last</th>
                      <th className="p-2 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.map((j) => {
                      const p = j.lead || j.contact || {};
                      const nextRetry = j.next_retry_at
                        ? new Date(j.next_retry_at).getTime() > Date.now()
                        : false;
                      return (
                        <tr key={j.id} className="border-t border-border hover:bg-muted/20"
                            data-testid={`vc-job-row-${j.id}`}>
                          <td className="p-2">
                            <div className="font-medium">{p.name || j.manual_name || "—"}</div>
                            {p.company_name && (
                              <div className="text-[10px] text-muted-foreground">{p.company_name}</div>
                            )}
                          </td>
                          <td className="p-2 font-mono text-[11px]">{p.phone || j.manual_phone || "—"}</td>
                          <td className="p-2">
                            <Badge className={`rounded-none text-[9px] border ${JOB_STATUS_TONE[j.status] || ""}`}>
                              {j.status}
                            </Badge>
                            {nextRetry && (
                              <div className="text-[9px] text-muted-foreground mt-0.5">
                                retry in {Math.max(0, Math.round((new Date(j.next_retry_at) - Date.now()) / 60000))}m
                              </div>
                            )}
                          </td>
                          <td className="p-2">{j.attempts || 0}</td>
                          <td className="p-2">
                            {j.outcome && (
                              <span className="text-[11px]">{OUTCOME_LABEL[j.outcome] || j.outcome}</span>
                            )}
                            {j.last_error && (
                              <div className="text-[9px] text-red-600 truncate max-w-[200px]"
                                   title={j.last_error}>
                                <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />
                                {j.last_error}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-[10px] text-muted-foreground">
                            {(j.last_dispatched_at || j.completed_at || j.claimed_at || "").slice(5, 16).replace("T", " ")}
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {j.whatsapp_sent_at ? (
                                <Badge className="rounded-none border bg-emerald-50 text-emerald-700 border-emerald-500/40 text-[9px] px-1.5"
                                        title={`Sent ${j.whatsapp_sent_at.slice(0,16).replace("T"," ")}${j.whatsapp_sent_by_name ? ` by ${j.whatsapp_sent_by_name}` : ""}`}
                                        data-testid={`vc-job-wa-sent-${j.id}`}>
                                  <Check className="w-2.5 h-2.5 mr-0.5" /> WA
                                </Badge>
                              ) : j.whatsapp_draft ? (
                                <Button size="sm" variant="outline"
                                         className={`rounded-none h-7 px-2 ${j.whatsapp_draft_interest ? "border-amber-500 text-amber-700" : "border-emerald-500 text-emerald-700"}`}
                                         onClick={() => setWaDraftJob(j)}
                                         title={j.whatsapp_draft_interest ? "Interested lead · review WhatsApp draft" : "Review WhatsApp follow-up draft"}
                                         data-testid={`vc-job-wa-review-${j.id}`}>
                                  <MessageSquare className="w-3 h-3 mr-1" />
                                  {j.whatsapp_draft_interest ? "Hot WA" : "WA"}
                                </Button>
                              ) : null}
                              <Button size="sm" variant="outline"
                                       className="rounded-none h-7 px-2"
                                       onClick={() => retryJob(j.id)}
                                       disabled={j.status === "in_progress"}
                                       data-testid={`vc-job-retry-${j.id}`}>
                                <RotateCw className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "settings" && campaign && (
          <div className="space-y-3">
            {campaign.channel !== "elevenlabs_convai" ? (
              <div className="text-xs text-muted-foreground p-4 border border-border">
                This campaign uses the human SDR channel (MCube). Auto-dispatch is only available for ElevenLabs AI campaigns.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between border border-border p-3">
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-indigo-600" /> Auto-dispatch
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Dial automatically every minute while this campaign is active.
                    </div>
                  </div>
                  <label className="cursor-pointer flex items-center gap-2">
                    <input type="checkbox"
                            checked={!!campaign.auto_process}
                            onChange={(e) => toggleAuto(e.target.checked)}
                            data-testid="vc-dash-auto-toggle" />
                    <span className="text-xs font-semibold">{campaign.auto_process ? "ON" : "OFF"}</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="border border-border p-3">
                    <Label className="label-eyebrow">Calls per minute</Label>
                    <Input type="number" min={1} max={10}
                            defaultValue={campaign.concurrency || 3}
                            onBlur={(e) => setConcurrency(Number(e.target.value))}
                            className="rounded-none h-9"
                            data-testid="vc-dash-concurrency" />
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Each tick dispatches up to this many ElevenLabs outbound calls.
                    </div>
                  </div>
                  <div className="border border-border p-3">
                    <Label className="label-eyebrow">Retry delay (minutes)</Label>
                    <Input type="number" min={1} max={1440}
                            defaultValue={campaign.retry_delay_minutes || 30}
                            onBlur={(e) => api.patch(
                              `/workspaces/${wsId}/voice-campaigns/${campaignId}`,
                              { retry_delay_minutes: Number(e.target.value) })
                              .then(load)
                              .catch(() => toast.error("Save failed"))}
                            className="rounded-none h-9"
                            data-testid="vc-dash-retry-delay" />
                    <div className="text-[10px] text-muted-foreground mt-1">
                      No-answer / busy / voicemail jobs wait this long before being retried.
                    </div>
                  </div>
                </div>
                <div className="border border-border p-3">
                  <Label className="label-eyebrow">Max attempts per lead</Label>
                  <Input type="number" min={1} max={10}
                          defaultValue={campaign.max_attempts_per_lead || 3}
                          onBlur={(e) => api.patch(
                            `/workspaces/${wsId}/voice-campaigns/${campaignId}`,
                            { max_attempts_per_lead: Number(e.target.value) })
                            .then(load)
                            .catch(() => toast.error("Save failed"))}
                          className="rounded-none h-9 max-w-[120px]"
                          data-testid="vc-dash-max-attempts" />
                </div>
              </>
            )}
          </div>
        )}

        {tab === "summary" && (
          <div className="space-y-3">
            {!summary && (
              <div className="text-xs text-muted-foreground p-4">Loading summary…</div>
            )}
            {summary && (
              <>
                <div className="grid grid-cols-4 gap-2">
                  <SummaryTile label="Connected" value={summary.connected} suffix={` (${summary.connect_rate}%)`} tone="emerald" />
                  <SummaryTile label="Meetings booked" value={summary.booked} suffix={` (${summary.booking_rate}%)`} tone="indigo" />
                  <SummaryTile label="Transfers to humans" value={summary.transfers} tone="blue" />
                  <SummaryTile label="Avg call duration" value={`${summary.avg_duration_sec || 0}s`} />
                </div>
                {summary.cost && (
                  <div className="grid grid-cols-3 gap-2" data-testid="campaign-cost-row">
                    <SummaryTile label="Call minutes" value={summary.total_minutes || 0} suffix=" min" />
                    <SummaryTile label={`Cost (${summary.cost.currency || "INR"})`}
                                   value={fmtMoney(summary.cost.total, summary.cost.currency)}
                                   tone="amber" />
                    <SummaryTile label="Rate" value={fmtMoney(summary.cost.per_minute, summary.cost.currency)} suffix="/min" />
                  </div>
                )}
                {summary.campaign?.budget_cap != null && (
                  <div className="border border-border p-3 space-y-1.5" data-testid="campaign-budget-bar">
                    {(() => {
                      const spent = Number(summary.cost?.total || 0);
                      const cap = Number(summary.campaign.budget_cap);
                      const pct = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0;
                      const hot = pct >= 100;
                      const warm = pct >= 80 && !hot;
                      const barCls = hot ? "bg-red-500" : warm ? "bg-amber-500" : "bg-emerald-500";
                      const cur = summary.cost?.currency || "INR";
                      return (
                        <>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold">Budget cap</span>
                            <span className={hot ? "text-red-600 font-semibold" : "text-foreground"}>
                              {fmtMoney(spent, cur)} / {fmtMoney(cap, cur)}
                              <span className="text-muted-foreground ml-1">({pct.toFixed(0)}%)</span>
                            </span>
                          </div>
                          <div className="h-2 bg-muted/60">
                            <div className={`h-full ${barCls} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                          {!hot && summary.budget_projection && (
                            <div className="text-[11px] text-amber-700 flex items-center gap-1"
                                 data-testid="campaign-budget-eta">
                              <AlertTriangle className="w-3 h-3" />
                              At the current pace you'll cross the cap in ~{summary.budget_projection.eta_minutes} min
                              <span className="text-muted-foreground ml-1">· {fmtMoney(summary.budget_projection.remaining, cur)} left</span>
                            </div>
                          )}
                          {summary.campaign.scheduled_resume_at && (
                            <div className="text-[11px] text-blue-700 flex items-center gap-1"
                                 data-testid="campaign-scheduled-resume">
                              <AlertTriangle className="w-3 h-3" />
                              Dialing paused until {new Date(summary.campaign.scheduled_resume_at).toLocaleString()}
                            </div>
                          )}
                          {summary.campaign.paused_reason === "budget_cap_hit" && (
                            <>
                              <div className="text-[11px] text-red-700 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Dispatcher paused — cap reached. Resume below or edit the cap.
                              </div>
                              <ResumeAfterCapActions wsId={wsId} cid={summary.campaign.id}
                                                        cap={cap}
                                                        onDone={load} />
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-border p-3">
                    <div className="label-eyebrow text-[10px] mb-2">Outcome breakdown</div>
                    {Object.entries(summary.outcome_counts || {}).length === 0 ? (
                      <div className="text-xs text-muted-foreground">No completed calls yet.</div>
                    ) : (
                      <div className="space-y-1">
                        {Object.entries(summary.outcome_counts).map(([k, v]) => {
                          const pct = summary.total ? Math.round((v / summary.total) * 100) : 0;
                          return (
                            <div key={k} className="text-xs">
                              <div className="flex items-center justify-between">
                                <span>{OUTCOME_LABEL[k] || k}</span>
                                <span className="font-mono">{v} · {pct}%</span>
                              </div>
                              <div className="h-1 bg-muted/40">
                                <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="border border-border p-3">
                    <div className="label-eyebrow text-[10px] mb-2">Failure reasons</div>
                    {Object.entries(summary.error_counts || {}).length === 0 ? (
                      <div className="text-xs text-muted-foreground">No errors recorded.</div>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {Object.entries(summary.error_counts).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                          <li key={k} className="flex items-center justify-between">
                            <span className="truncate max-w-[200px]" title={k}>
                              <AlertTriangle className="w-3 h-3 inline mr-1 text-red-500" />
                              {k}
                            </span>
                            <span className="font-mono">{v}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {summary.leaderboard?.length > 0 && (
                  <div className="border border-border p-3">
                    <div className="label-eyebrow text-[10px] mb-2">Caller leaderboard</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-muted-foreground">
                          <th className="pb-1">Caller</th>
                          <th className="pb-1 text-right">Completed</th>
                          <th className="pb-1 text-right">Booked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.leaderboard.map((r, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="py-1">{r.name}</td>
                            <td className="py-1 text-right font-mono">{r.total}</td>
                            <td className="py-1 text-right font-mono text-emerald-700">{r.booked}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground">
                  Attempts total: <b>{summary.attempts_sum || 0}</b> ·
                  Started {(summary.campaign?.started_at || "—").slice(0, 16).replace("T", " ")} ·
                  {summary.campaign?.completed_at
                    ? ` Completed ${summary.campaign.completed_at.slice(0, 16).replace("T", " ")}`
                    : " In progress"}
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
      {waDraftJob && (
        <WhatsappDraftDialog
          wsId={wsId} job={waDraftJob}
          open
          onClose={(res) => {
            setWaDraftJob(null);
            // Any save or send mutates the job row — refresh.
            if (res?.sent || res?.touched) load();
          }}
        />
      )}
    </Dialog>
  );
}


function SummaryTile({ label, value, suffix, tone }) {
  const toneCls = {
    emerald: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
    indigo: "border-indigo-500/40 text-indigo-700 bg-indigo-50",
    blue: "border-blue-500/40 text-blue-700 bg-blue-50",
  }[tone] || "border-border bg-muted/20 text-foreground";
  return (
    <div className={`border p-3 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold">{value ?? 0}{suffix || ""}</div>
    </div>
  );
}



/** Red-strip action row shown when a campaign is paused for
 *  budget_cap_hit. Three one-click paths: remove the cap outright,
 *  double it, or edit it inline. Each hits the dedicated
 *  `resume-after-cap` endpoint so the state flip is atomic. */
function ResumeAfterCapActions({ wsId, cid, cap, onDone }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // null | "edit" | "schedule"
  const [newCap, setNewCap] = useState(cap ? cap * 2 : "");
  const [resumeAt, setResumeAt] = useState(() => {
    // Default: today 14:00 local → ISO
    const d = new Date();
    d.setHours(14, 0, 0, 0);
    // toISOString strips the TZ offset, but Mongo + the backend
    // comparator sort these as strings, so UTC is fine.
    return d.toISOString().slice(0, 16);
  });
  const call = async (strategy, extra = {}) => {
    setBusy(true);
    try {
      await api.post(
        `/workspaces/${wsId}/voice-campaigns/${cid}/resume-after-cap`,
        { strategy, ...extra });
      const sched = extra.resume_at;
      toast.success(
        strategy === "remove"
          ? (sched ? `Cap removed · dialing resumes at ${sched}` : "Cap removed · campaign resumed")
          : strategy === "double"
          ? (sched ? `Cap doubled · dialing resumes at ${sched}` : `Cap doubled to \u20B9${(cap * 2).toFixed(0)} · resumed`)
          : (sched ? `Cap updated · dialing resumes at ${sched}` : "Cap updated · resumed"));
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Resume failed"));
    } finally { setBusy(false); }
  };
  if (mode === "edit") {
    return (
      <div className="flex items-center gap-2 pt-1 flex-wrap" data-testid="resume-after-cap-edit">
        <Input type="number" min={0} step={1}
                value={newCap}
                onChange={(e) => setNewCap(e.target.value)}
                className="rounded-none h-8 w-28"
                data-testid="resume-after-cap-new-cap" />
        <Button size="sm" className="rounded-none h-8" disabled={busy || !newCap}
                 data-testid="resume-after-cap-save"
                 onClick={() => call("set", { new_cap: Number(newCap) })}>
          Set & resume
        </Button>
        <Button size="sm" variant="outline" className="rounded-none h-8"
                 onClick={() => setMode(null)}>
          Cancel
        </Button>
      </div>
    );
  }
  if (mode === "schedule") {
    // Quick-chip presets so the user doesn't fiddle with the
    // datetime picker for the common "later today / tomorrow 9am
    // / Monday 9am" paths.
    const presets = (() => {
      const d = (fn) => {
        const x = new Date();
        fn(x);
        return x.toISOString().slice(0, 16);
      };
      return [
        { label: "In 1 hour", at: d((x) => x.setHours(x.getHours() + 1, 0, 0, 0)) },
        { label: "Today 14:00", at: d((x) => x.setHours(14, 0, 0, 0)) },
        { label: "Tomorrow 09:00", at: d((x) => { x.setDate(x.getDate() + 1); x.setHours(9, 0, 0, 0); }) },
        { label: "Monday 09:00", at: d((x) => {
            // 1 = Monday … 0 = Sunday. Jump to the next Monday
            // (never today, even if today is already Monday).
            const day = x.getDay();
            const delta = ((8 - day) % 7) || 7;
            x.setDate(x.getDate() + delta);
            x.setHours(9, 0, 0, 0);
          }) },
      ];
    })();
    return (
      <div className="space-y-1 pt-1" data-testid="resume-after-cap-schedule">
        <div className="text-[11px] text-muted-foreground">
          Resume dialing at this wall-clock time (your browser's timezone):
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setResumeAt(p.at)}
              data-testid={`resume-schedule-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[10px] px-1.5 py-0.5 border transition ${
                resumeAt === p.at
                  ? "bg-emerald-600 text-white border-emerald-700"
                  : "border-border hover:border-emerald-600 hover:text-emerald-700"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="datetime-local"
                  value={resumeAt}
                  onChange={(e) => setResumeAt(e.target.value)}
                  className="rounded-none h-8 w-48"
                  data-testid="resume-after-cap-datetime" />
          <Button size="sm" className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700"
                   disabled={busy || !resumeAt}
                   data-testid="resume-after-cap-schedule-remove"
                   onClick={() => call("remove", {
                     resume_at: new Date(resumeAt).toISOString(),
                   })}>
            Remove cap + schedule
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8"
                   disabled={busy || !resumeAt || !cap}
                   data-testid="resume-after-cap-schedule-double"
                   onClick={() => call("double", {
                     resume_at: new Date(resumeAt).toISOString(),
                   })}>
            Double cap + schedule
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8"
                   onClick={() => setMode(null)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 pt-1 flex-wrap" data-testid="resume-after-cap-actions">
      <Button size="sm" className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-700"
               disabled={busy}
               data-testid="resume-after-cap-remove"
               onClick={() => call("remove")}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
        Resume (remove cap)
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-7"
               disabled={busy || !cap}
               data-testid="resume-after-cap-double"
               onClick={() => call("double")}>
        Double cap & resume (\u20B9{(cap * 2).toFixed(0)})
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-7"
               disabled={busy}
               data-testid="resume-after-cap-edit-btn"
               onClick={() => setMode("edit")}>
        Edit cap…
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-7"
               disabled={busy}
               data-testid="resume-after-cap-schedule-btn"
               onClick={() => setMode("schedule")}>
        Resume + schedule…
      </Button>
    </div>
  );
}
